import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import KpiCard from '../shared/KpiCard';
import { formatNum, CHART_COLORS } from '../shared/formatters';
import constants from '../../../config';

class AnalyticsDemand extends Component {
  render() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var data = this.props.data;
    if (!data) return <div className="tw-text-center tw-py-16 tw-text-[#999]">No data available.</div>;

    var dailyOrders = data.dailyOrders || [];
    var hourlyOrders = data.hourlyOrders || [];
    var offerStats = data.offerStats || [];
    var topBuyers = data.topBuyers || [];
    var topBuylistCards = data.topBuylistCards || [];
    var self = this;

    // Format offer stats for pie
    var offerStatusLabels = { pending: 'Pending', accepted: 'Accepted', declined: 'Declined', countered: 'Countered', expired: 'Expired' };
    var offerPie = offerStats.map(function(o) {
      return { name: offerStatusLabels[o.status] || o.status, value: o.count };
    });

    // Format hourly orders — pad all 24 hours
    var hourMap = {};
    hourlyOrders.forEach(function(h) { hourMap[h.hour] = h.count; });
    var hourlyData = [];
    for (var h = 0; h < 24; h++) {
      var label = h === 0 ? '12am' : h < 12 ? h + 'am' : h === 12 ? '12pm' : (h - 12) + 'pm';
      hourlyData.push({ hour: label, count: hourMap[h] || 0 });
    }

    return (
      <div>
        {/* KPI Cards */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 tw-mb-6">
          <KpiCard icon="fa-users" label="Total Buyers" value={data.totalBuyers} color="#3B82F6" change="in period" />
          <KpiCard icon="fa-repeat" label="Repeat Rate" value={data.repeatBuyerRate + '%'} color="#10B981" change="all time" />
          <KpiCard icon="fa-handshake-o" label="Total Offers" value={data.totalOffers} color="#D4A843" change="in period" />
          <KpiCard icon="fa-percent" label="Avg Offer Discount" value={data.avgOfferDiscount + '%'} color="#8B5CF6" />
          <KpiCard icon="fa-list-alt" label="Buylist Entries" value={data.buylistActive} color="#F59E0B" change="active" />
          <KpiCard icon="fa-dollar" label="Buylist Value" value={'$' + formatNum(data.buylistValue)} color="#EF4444" />
        </div>

        {/* Daily Orders Chart */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Daily Orders</h3>
          {dailyOrders.length > 0 ? (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={dailyOrders} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="demandGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return v.slice(5); }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                  <Area type="monotone" dataKey="count" name="Orders" stroke="#3B82F6" strokeWidth={2.5} fill="url(#demandGrad)" dot={false} activeDot={{ r: 5, fill: '#3B82F6' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No order data yet</div>
          )}
        </div>

        {/* Row: Hourly Orders + Offer Stats + Top Buyers */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          {/* Hourly Orders */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Orders by Hour</h3>
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <BarChart data={hourlyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#999' }} interval={2} />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                  <Bar dataKey="count" name="Orders" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Offer Status Breakdown */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Offer Outcomes</h3>
            {offerPie.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={offerPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={35} paddingAngle={2}>
                        {offerPie.map(function(e, i) { return <Cell key={'os-' + i} fill={CHART_COLORS[i % CHART_COLORS.length]} />; })}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-space-y-1 tw-mt-2">
                  {offerPie.map(function(o, i) {
                    return (
                      <div key={'osl-' + i} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                        <span className="tw-flex tw-items-center tw-gap-2">
                          <span className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-inline-block" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="tw-text-[#666] dark:tw-text-[#aaa]">{o.name}</span>
                        </span>
                        <span className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{o.value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No offers yet</div>
            )}
          </div>

          {/* Top Buyers by LTV */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Top Buyers (LTV)</h3>
            {topBuyers.length > 0 ? (
              <div className="tw-space-y-0">
                {topBuyers.map(function(b, i) {
                  return (
                    <div key={'tb-' + i} className="tw-flex tw-items-center tw-gap-3 tw-py-2 tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222] last:tw-border-0">
                      <img
                        src={b.profilePic ? (constants.DOMAIN + '/' + b.profilePic.replace(/ /g, '%20')) : '/default-avatar.png'}
                        alt=""
                        className="tw-w-7 tw-h-7 tw-rounded-full tw-object-cover tw-flex-shrink-0"
                      />
                      <button
                        onClick={function() { self.props.onOpenUserModal && self.props.onOpenUserModal(b._id); }}
                        className="tw-flex-1 tw-min-w-0 tw-text-left tw-text-xs tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 hover:tw-text-[#D4A843]"
                      >
                        {b.username || 'Unknown'}
                      </button>
                      <div className="tw-text-right tw-flex-shrink-0">
                        <div className="tw-text-xs tw-font-semibold" style={{ color: '#D4A843' }}>${formatNum(b.totalSpent)}</div>
                        <div className="tw-text-[10px] tw-text-[#999]">{b.orders} orders</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No buyers yet</div>
            )}
          </div>
        </div>

        {/* Buylist Demand — Top Cards */}
        <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Top Buylist Cards (Demand)</h3>
          {topBuylistCards.length > 0 ? (
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-text-sm">
                <thead>
                  <tr className="tw-border-b tw-border-[#d0d0d0] dark:tw-border-[#555]">
                    <th className="tw-text-left tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Card</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Wanted Qty</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Buyers</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Avg Price</th>
                  </tr>
                </thead>
                <tbody>
                  {topBuylistCards.map(function(card, i) {
                    return (
                      <tr key={'blc-' + i} className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222]">
                        <td className="tw-py-2 tw-px-2">
                          <div className="tw-flex tw-items-center tw-gap-2">
                            {card.image && <img src={card.image.startsWith('http') ? card.image : constants.DOMAIN + '/' + card.image} alt="" className="tw-w-8 tw-h-10 tw-object-contain tw-rounded" />}
                            <div>
                              <div className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{card.name}</div>
                              <div className="tw-text-xs tw-text-[#999]">{card.cardNumber}</div>
                            </div>
                          </div>
                        </td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{card.totalQty}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-text-[#666] dark:tw-text-[#aaa]">{card.buyers}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold" style={{ color: '#D4A843' }}>${card.avgPrice.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No buylist data</div>
          )}
        </div>
      </div>
    );
  }
}

export default AnalyticsDemand;
